import * as React from "react";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";

type Copy = {
  module: string;
  backgroundImage: string;
  titleEn: string;
  titleDe: string;
  scenarioEn: string;
  scenarioDe: string;
  taskEn: string;
  taskDe: string;
};

export default function PrepareScenarioScreen({
  handlePageChange,
  nextPage,
  backPage,
  copy,
}: {
  handlePageChange: (page: string) => void;
  nextPage: string;
  backPage: string;
  copy: Copy;
}) {
  const { lang } = useI18n();
  const isDE = lang === "de";

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 48px)",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
        backgroundImage: `
          linear-gradient(
            rgba(8, 8, 8, 0.78),
            rgba(8, 8, 8, 0.82)
          ),
          url("${copy.backgroundImage}")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: { xs: "scroll", md: "fixed" },
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", sm: "min(88vw, 820px)" },
          borderRadius: 5,
          p: { xs: 4, sm: 6 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49,32,32,0.95), rgba(34,21,21,0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
          textAlign: "center",
        }}
      >
        <Stack spacing={3.5} alignItems="center">
          <Chip
            label={`${copy.module}`}
            sx={{
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.25)",
              backgroundColor: "rgba(255,255,255,0.08)",
              fontWeight: 900,
              letterSpacing: "0.08em",
            }}
          />

          <Typography
            sx={{
              color: "#fff",
              fontSize: { xs: "1.8rem", sm: "2.45rem" },
              fontWeight: 950,
              lineHeight: 1.15,
            }}
          >
            {isDE ? copy.titleDe : copy.titleEn}
          </Typography>

          <Box sx={{ maxWidth: 660 }}>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.62)",
                fontWeight: 900,
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              {isDE ? "Szenario" : "Scenario"}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.84)", lineHeight: 1.7 }}>
              {isDE ? copy.scenarioDe : copy.scenarioEn}
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",
              maxWidth: 660,
              p: 3,
              borderRadius: 4,
              backgroundColor: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.14)",
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 900,
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              {isDE ? "Praktische Aufgabe" : "Practical task"}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.82)", lineHeight: 1.65 }}>
              {isDE ? copy.taskDe : copy.taskEn}
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="outlined"
              onClick={() => handlePageChange(backPage)}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
                px: 4,
                py: 1.25,
                borderColor: "rgba(255,255,255,0.4)",
                color: "#fff",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              {isDE ? "Zurück" : "Back"}
            </Button>

            <Button
              variant="contained"
              onClick={() => handlePageChange(nextPage)}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
                px: 4,
                py: 1.25,
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
                "&:hover": {
                  background: "linear-gradient(90deg, #9f0b1c, #c32136)",
                },
              }}
            >
              {isDE ? "Zur Frage" : "Continue to question"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
